/**
 * Mermaid Tool - Mermaid 渲染模組
 * static/js/mermaid/renderer.js
 */

import { getState, updateState } from './state.js';
import { DEBOUNCE_DELAY } from './config.js';
import { updateRenderStatus, showToast } from './ui.js';

// Mermaid 渲染 ID 計數器
let mermaidIdCounter = 0;

/**
 * 初始化 Mermaid 函式庫
 */
export function initMermaid() {
    if (typeof mermaid === 'undefined') {
        console.error('Mermaid library not loaded');
        return;
    }
    
    mermaid.initialize({
        startOnLoad: false,
        theme: 'default',
        securityLevel: 'loose',
        flowchart: {
            useMaxWidth: true,
            htmlLabels: true
        }
    });
}

/**
 * 渲染 Mermaid 圖表
 * @param {string} code - Mermaid 程式碼
 * @param {HTMLElement} container - 輸出容器
 * @returns {Promise<boolean>} 渲染是否成功
 */
export async function renderDiagram(code, container) {
    if (!code || !container) {
        return false;
    }
    
    const state = getState();
    
    // 如果程式碼沒變，不重新渲染
    if (code === state.lastRenderCode) {
        return true;
    }
    
    updateState({ isRendering: true });
    updateRenderStatus('rendering');
    
    try {
        // 清理舊的 Mermaid 錯誤元素
        cleanupMermaidErrors();
        
        // 生成唯一 ID
        const id = `mermaid-svg-${++mermaidIdCounter}`;
        
        // 渲染
        const { svg } = await mermaid.render(id, code);
        
        container.innerHTML = svg;
        
        // 更新狀態
        updateState({ 
            isRendering: false,
            lastRenderCode: code 
        });
        updateRenderStatus('ready');
        
        return true;
        
    } catch (error) {
        console.error('Mermaid render error:', error);
        
        // 清理錯誤訊息
        cleanupMermaidErrors();
        
        // 顯示錯誤
        const errorMessage = extractErrorMessage(error);
        container.innerHTML = `
            <div class="alert alert-danger m-3">
                <i class="fas fa-exclamation-triangle me-2"></i>
                <strong>渲染錯誤</strong>
                <pre class="mt-2 mb-0 small">${errorMessage}</pre>
            </div>
        `;
        
        updateState({ isRendering: false });
        updateRenderStatus('error', '語法錯誤');
        
        return false;
    }
}

/**
 * 使用防抖渲染圖表
 * @param {string} code - Mermaid 程式碼
 * @param {HTMLElement} container - 輸出容器
 * @param {number} delay - 防抖延遲（毫秒）
 */
export function renderDiagramDebounced(code, container, delay = DEBOUNCE_DELAY) {
    const state = getState();
    
    // 清除先前的計時器
    if (state.renderTimeout) {
        clearTimeout(state.renderTimeout);
    }
    
    // 設定新的計時器
    const timeout = setTimeout(() => {
        renderDiagram(code, container);
    }, delay);
    
    updateState({ renderTimeout: timeout });
}

/**
 * 清理 Mermaid 產生的孤立錯誤元素
 */
export function cleanupMermaidErrors() {
    // 清理 body 直接子元素中的 Mermaid 錯誤
    const errorElements = document.querySelectorAll('body > #d, body > [id^="mermaid-"]');
    errorElements.forEach(el => {
        // 確認是 Mermaid 產生的元素（通常是空的或包含錯誤）
        if (el.id === 'd' || el.innerHTML === '' || el.classList.contains('mermaid')) {
            el.remove();
        }
    });
    
    // 清理可能殘留的 SVG 容器
    const orphanedSvgs = document.querySelectorAll('body > svg[id^="mermaid"]');
    orphanedSvgs.forEach(el => el.remove());
}

/**
 * 從 Mermaid 錯誤中提取可讀的錯誤訊息
 * @param {Error} error - 錯誤物件
 * @returns {string} 錯誤訊息
 */
function extractErrorMessage(error) {
    if (!error) return '未知錯誤';
    
    let message = error.message || String(error);
    
    // 移除過長的堆疊追蹤
    const stackIndex = message.indexOf('at ');
    if (stackIndex > 0) {
        message = message.substring(0, stackIndex);
    }
    
    // 擷取最有用的部分
    const lines = message.split('\n').filter(line => line.trim());
    if (lines.length > 3) {
        message = lines.slice(0, 3).join('\n') + '\n...';
    }
    
    return message.trim();
}

/**
 * 偵測程式碼中的圖表類型
 * @param {string} code - Mermaid 程式碼
 * @returns {string} 圖表類型
 */
export function detectDiagramType(code) {
    if (!code) return 'unknown';
    
    const firstLine = code.trim().split('\n')[0].toLowerCase();
    
    const typePatterns = [
        { pattern: /^flowchart|^graph/, type: 'flowchart' },
        { pattern: /^sequencediagram/, type: 'sequence' },
        { pattern: /^gantt/, type: 'gantt' },
        { pattern: /^pie/, type: 'pie' },
        { pattern: /^mindmap/, type: 'mindmap' },
        { pattern: /^timeline/, type: 'timeline' },
        { pattern: /^statediagram/, type: 'state' },
        { pattern: /^classdiagram/, type: 'class' },
        { pattern: /^erdiagram/, type: 'er' },
        { pattern: /^journey/, type: 'journey' },
        { pattern: /^gitgraph/, type: 'git' },
        { pattern: /^quadrantchart/, type: 'quadrant' },
        { pattern: /^xychart/, type: 'xychart' },
        { pattern: /^radar/, type: 'radar' },
        { pattern: /^block/, type: 'block' },
        { pattern: /^packet/, type: 'packet' },
        { pattern: /^architecture/, type: 'architecture' }
    ];
    
    for (const { pattern, type } of typePatterns) {
        if (pattern.test(firstLine)) {
            return type;
        }
    }
    
    return 'unknown';
}

/**
 * 檢查程式碼是否為流程圖類型
 * @param {string} code - Mermaid 程式碼
 * @returns {boolean}
 */
export function isFlowchartType(code) {
    if (!code) return false;
    return /^(flowchart|graph)\s+(TD|TB|BT|LR|RL)/im.test(code.trim());
}

/**
 * 取得流程圖的方向
 * @param {string} code - Mermaid 程式碼
 * @returns {string|null} 方向（TD, BT, LR, RL）
 */
export function getFlowchartDirection(code) {
    if (!code) return null;
    const match = code.match(/^(flowchart|graph)\s+(TD|TB|BT|LR|RL)/im);
    return match ? match[2].toUpperCase() : null;
}

/**
 * 變更流程圖方向
 * @param {string} code - 原始程式碼
 * @param {string} newDirection - 新方向
 * @returns {string} 更新後的程式碼
 */
export function changeFlowchartDirection(code, newDirection) {
    if (!code) return code;
    
    return code.replace(
        /^(flowchart|graph)\s+(TD|TB|BT|LR|RL)/im,
        `$1 ${newDirection}`
    );
}
